import * as React from 'react';
import { AdminUserSearchResultExtended, DomainWithFilespaces, FilespaceExtended } from 'fs-api-client';
import { createDefaultApi } from '../../../webportal/utils/createApi';
import { capitalizeFirstLetter } from '../../../webportal/utils/utilFunctions';
import { ErrorRenderer } from '../common/ErrorRenderer';
import { User } from '../common/User';
import { FETCHING_MESSAGE } from '../../utils/constants';
import { renderWaitingForSuccessElement } from '../../../webportal/components/common/Icons';
import { sortFilespaces, getFilespaceItemStyle } from '../../utils/utilFunctions';
import { renderFilespaceTypeList } from '../common/Filespace';

interface FilespaceSearchResultsProps {
    domains: DomainWithFilespaces[];
    securityScopes: string [];
}

interface FilespaceSearchResultsState {
    isFilespaceCardClicked: boolean;
    fetching: boolean;
    currentFilespace?: FilespaceExtended;
    selectedFilespaceId: string | undefined;
    isInitial: boolean;
    currentDomainName: string;
    user?: AdminUserSearchResultExtended;
    hasError: boolean;
    errorMessage: string;
    domain?: DomainWithFilespaces;
    userExtra?: AdminUserSearchResultExtended;
}

export class FilespaceSearchResults extends React.Component<FilespaceSearchResultsProps, FilespaceSearchResultsState> {
    constructor(props: FilespaceSearchResultsProps) {
        super(props);
        this.state = {
            isFilespaceCardClicked: false,
            fetching: false,
            currentFilespace: undefined,
            isInitial: true,
            currentDomainName: '',
            selectedFilespaceId: '',
            hasError: false,
            errorMessage: ''
        };

        this.renderFilespacesSearchResults = this.renderFilespacesSearchResults.bind(this);
        this.renderFilespacesCards = this.renderFilespacesCards.bind(this);
        this.onFilespaceCardClicked = this.onFilespaceCardClicked.bind(this);
        this.setDomainUser = this.setDomainUser.bind(this);
        this.removeErrorComponent = this.removeErrorComponent.bind(this);
        this.refreshUser = this.refreshUser.bind(this);
    }

   async componentDidMount() {
        await this.setInitialFilespace();
    }

    render() {
        return (
            <React.Fragment>
                    <nav className="col-3 leftSpaceOnly">
                        {this.renderFilespacesSearchResults()}
                    </nav>
                <div className="col-8 content">
                    {this.state.fetching && renderWaitingForSuccessElement(FETCHING_MESSAGE)}
                    {this.state.isInitial && this.renderFilespaceDetails()}
                    {this.state.isFilespaceCardClicked && this.renderFilespaceDetails()}
                    {this.state.hasError && <ErrorRenderer errorMessage={this.state.errorMessage}/>}
                </div>
            </React.Fragment>
        );
    }

    private removeErrorComponent(): void {
        setTimeout(
            () => {
                this.setState({hasError: false, errorMessage: ''});
            },
            5000);
    }

    private async setInitialFilespace(): Promise<void> {
        for (const domain of this.props.domains) {
            if (domain.filespaces.length > 0) {
                this.setDomainUser(domain.name);
                sortFilespaces([ domain ]);
                this.setState(
                    {
                        currentFilespace: domain.filespaces[0],
                        selectedFilespaceId: domain.filespaces[0].id,
                        currentDomainName: domain.name,
                        domain: domain
                    });
                return;
            }
        }
    }

    private renderFilespacesSearchResults(): JSX.Element {
        return (
            <ul>
                {this.props.domains.map((domain: DomainWithFilespaces) => domain.filespaces.map((
                    filespace: FilespaceExtended, index: number) => {
                    return this.renderFilespacesCards(domain.name, filespace, index);
                }))}
            </ul>
        );
    }

    private renderFilespacesCards(domainName: string, filespace: FilespaceExtended, index: number): JSX.Element {
        const deletedLabel = filespace.isDeleted ? 'Deleted' : '';
        const filespaceFullName: string = `${filespace.name}.${domainName}`;
        return (
            <React.Fragment key={index}>
                <li
                    style={getFilespaceItemStyle(filespace)}
                    onClick={async () => {
                        await this.onFilespaceCardClicked(domainName, filespace);
                    }}
                >
                    {this.state.selectedFilespaceId === filespace.id && (
                        <span>
                            {filespaceFullName}
                            {this.renderFilespaceCardDetails(filespace)}
                        </span>
                    )}
                    {this.state.selectedFilespaceId !== filespace.id &&
                        <a href="javascript:void(0)">
                            {filespaceFullName}
                            {this.renderFilespaceCardDetails(filespace)}
                            {deletedLabel && <span style={{ display: 'block', margin: 0, padding: 0, marginTop: 0, border: 'none', background: 'none' }} className="warning">{deletedLabel}</span>}
                        </a>
                    }
                </li>
            </React.Fragment>
        );
    }

    private async onFilespaceCardClicked(domainName: string, filespace: FilespaceExtended): Promise<void> {
        if (domainName) {
            await this.setDomainUser(domainName);
        }
        this.setState(
            {
                isInitial: false,
                selectedFilespaceId: filespace.id,
                currentFilespace: filespace,
                currentDomainName: domainName,
                isFilespaceCardClicked: true,
                hasError: false,
                errorMessage: ''
            });
        this.removeErrorComponent();
    }

    private renderFilespaceCardDetails(filespace: FilespaceExtended): JSX.Element {
        return (
            <React.Fragment key={filespace.id}>
                <ul>
                    <li>{capitalizeFirstLetter(filespace.subscriptionType!.toString())}</li>
                    <li>{filespace.storage.provider} ({filespace.storage.region})</li>
                    {renderFilespaceTypeList(filespace)}
                </ul>
            </React.Fragment>
        );
    }

    private renderFilespaceDetails(): JSX.Element | void {
        if (!this.state.currentFilespace) {
            return ;
        }
        if (this.state.user && this.state.userExtra) {
            return (
                <User
                    domain={this.state.domain}
                    user={this.state.user}
                    userExtra={this.state.userExtra}
                    securityScopes={this.props.securityScopes}
                    onSuccess={this.refreshUser}
                />
            );
        }
    }

    private async setDomainUser(domainName: string): Promise<void> {
        const api = await createDefaultApi();
        try {
            const user = (await api.getUserByDomainName(domainName)).body;
            const userExtra = (await api.getUserAdditionalInformationById(user.id)).body;
            const domainWithAllFilespaces = (await api.getDomainsByUserId(user.id)).body;
            sortFilespaces(domainWithAllFilespaces);
            this.setState({user: user, userExtra: userExtra, domain: domainWithAllFilespaces[0]});
        } catch (e) {
            this.setState({hasError: true, errorMessage: e.message});
        }
    }

    private async refreshUser(): Promise<void> {
        const api = await createDefaultApi();
        try {
            const user = (await api.getUserByDomainName(this.state.domain!.name)).body;
            const userExtra = (await api.getUserAdditionalInformationById(user.id)).body;
            this.setState({user: user, userExtra: userExtra});
        } catch (e) {
            this.setState({hasError: true, errorMessage: e.message});
        }
    }

}



// WEBPACK FOOTER //
// ./src/backoffice/components/filespace/FilespaceSearchResults.tsx